import type { RoutePattern } from '@/services/router/match-route';
import { matchRoutes } from '@/services/router/match-route';
import type { ProductTaxonomy } from '@/services/product-taxonomy';
import { normalizeJourneysPathname } from '@/services/product-taxonomy/routes';

/**
 * Resolved campaign/automation noun forms for the current account, derived from
 * the product taxonomy. Suggestion copy reads these so it renders "campaign" or
 * "automation" per the journeys rename flag — with the correct article.
 */
interface CampaignCopy {
  singular: string; // campaign / automation
  plural: string; // campaigns / automations
  titleSingular: string; // Campaign / Automation
  titlePlural: string; // Campaigns / Automations
  indefiniteSingular: string; // a campaign / an automation
  indefiniteTitleSingular: string; // a Campaign / an Automation
}

/**
 * Resolved person/profile noun forms for the current account, derived from the
 * product taxonomy. Suggestion copy reads these so it renders "person"/"people"
 * or "profile"/"profiles" per the journeys rename flag.
 */
interface PeopleCopy {
  singular: string; // person / profile
  plural: string; // people / profiles
  titleSingular: string; // Person / Profile
  titlePlural: string; // People / Profiles
  relativePronoun: string; // who / that
}

/**
 * Resolved customer/profile noun forms for the current account, derived from the
 * product taxonomy. Suggestion copy reads these so it renders "customer"/"customers"
 * or "profile"/"profiles" per the journeys_taxonomy_rename flag.
 */
interface CustomersCopy {
  singular: string; // customer / profile
  plural: string; // customers / profiles
}

/** Context the agent's static-suggestion builders and exclusion predicates can read. */
interface SuggestionContext {
  isBuilder: boolean;
  /** `show-goals` (FLO-4772). Off hides goals across the product, goal pitches included. */
  showGoals: boolean;
  campaign: CampaignCopy;
  people: PeopleCopy;
  customers: CustomersCopy;
}

/** A suggestion's copy is either a fixed string or built from the account's taxonomy. */
type SuggestionCopy = string | ((context: SuggestionContext) => string);

interface Suggestion {
  label: SuggestionCopy;
  message: SuggestionCopy;
  /**
   * Suggestions sharing a group are alternative pitches for one capability;
   * only one of them is drawn per render.
   */
  group?: string;
  /**
   * Hard exclude predicate. Use for plan gating or any other "never show this"
   * condition. Filtered out before the suggestion enters the marquee or the
   * next-message pool.
   */
  excludeIf?: (context: SuggestionContext) => boolean;
}

/** Convenience predicate for `excludeIf` on suggestions that depend on Builder-restricted features. */
const isPlanBuilder = (context: SuggestionContext): boolean => context.isBuilder;

/** Convenience predicate for `excludeIf` on suggestions that pitch goals (FLO-4772). */
const isGoalsHidden = (context: SuggestionContext): boolean => !context.showGoals;

/**
 * Marks every suggestion in a list as goals-only. With `show-goals` off the goals routes render
 * NotFoundPage, so a goals-scoped route group would otherwise offer "What are goals?" beside a
 * 404; excluding the whole pool makes the draw fall back to the generic suggestions. Applied as a
 * wrapper rather than a line per suggestion so a pitch added to these groups later inherits the
 * gate instead of quietly escaping it. Composes with any predicate a suggestion already carries.
 */
function goalsOnly(suggestions: Suggestion[]): Suggestion[] {
  return suggestions.map((suggestion) => ({
    ...suggestion,
    excludeIf: (context: SuggestionContext) => isGoalsHidden(context) || (suggestion.excludeIf?.(context) ?? false),
  }));
}

/** The LLM-action pitches on the workflow builder. */
export const LLM_ACTION_GROUP = 'llm-action';

const indefiniteArticle = (noun: string): string => (/^[aeiou]/i.test(noun) ? 'an' : 'a');

/** Builds the resolved campaign noun forms for the current account from the product taxonomy. */
function buildCampaignCopy(taxonomy: ProductTaxonomy): CampaignCopy {
  const singular = taxonomy.term('campaigns.singular');
  const titleSingular = taxonomy.term('campaigns.titleSingular');

  return {
    singular,
    plural: taxonomy.term('campaigns.plural'),
    titleSingular,
    titlePlural: taxonomy.term('campaigns.titlePlural'),
    indefiniteSingular: `${indefiniteArticle(singular)} ${singular}`,
    indefiniteTitleSingular: `${indefiniteArticle(titleSingular)} ${titleSingular}`,
  };
}

/** Builds the resolved person noun forms for the current account from the product taxonomy. */
function buildPeopleCopy(taxonomy: ProductTaxonomy): PeopleCopy {
  return {
    singular: taxonomy.term('people.singular'),
    plural: taxonomy.term('people.plural'),
    titleSingular: taxonomy.term('people.titleSingular'),
    titlePlural: taxonomy.term('people.titlePlural'),
    relativePronoun: taxonomy.t('people.relativePronoun'),
  };
}

/** Builds the resolved customer noun forms for the current account from the product taxonomy. */
function buildCustomersCopy(taxonomy: ProductTaxonomy): CustomersCopy {
  return {
    singular: taxonomy.term('customers.singular'),
    plural: taxonomy.term('customers.plural'),
  };
}

function resolveCopy(copy: SuggestionCopy, context: SuggestionContext): string {
  return typeof copy === 'function' ? copy(context) : copy;
}

export interface ResolvedSuggestion {
  label: string;
  message: string;
  group?: string;
}

function resolveSuggestion(suggestion: Suggestion, context: SuggestionContext): ResolvedSuggestion {
  return {
    label: resolveCopy(suggestion.label, context),
    message: resolveCopy(suggestion.message, context),
    group: suggestion.group,
  };
}

/**
 * Static list of generic suggestions shown when no route-specific suggestions apply.
 */
const STATIC_SUGGESTIONS: Suggestion[] = [
  {
    label: (context) => `Create ${context.campaign.indefiniteSingular}`,
    message: (context) =>
      `I'd like to create ${context.campaign.indefiniteSingular}. Can you ask me a few questions to understand my goal, audience, and channel before we get started?`,
    excludeIf: isPlanBuilder,
  },
  {
    label: 'What are triggers?',
    message: (context) => `What are ${context.campaign.singular} triggers and how do they work?`,
    excludeIf: isPlanBuilder,
  },
  {
    label: 'Build a segment',
    message: 'I want to build a segment. Can you ask me questions to help define the right audience criteria?',
  },
  {
    label: 'What is a journey?',
    message: (context) => `What is a ${context.customers.singular} journey in Customer.io?`,
  },
  {
    label: 'Draft a broadcast',
    message:
      'I want to draft a broadcast. Can you ask me about my audience, goal, and message before writing anything?',
  },
  { label: 'How do I track events?', message: 'How do I track custom events in Customer.io?' },
  {
    label: (context) => `Brainstorm ${context.campaign.indefiniteSingular}`,
    message: (context) =>
      `I want to brainstorm a new ${context.campaign.singular} idea. Can you ask me about my goals and audience to help generate the right concepts?`,
    excludeIf: isPlanBuilder,
  },
  { label: 'What are webhooks?', message: 'What are webhooks and how do I configure them?' },
  {
    label: 'Create a welcome series',
    message:
      "I'd like to create a welcome series for new profiles. What questions do you have to help me design the right flow?",
    excludeIf: isPlanBuilder,
  },
  { label: 'What is personalization?', message: 'What is message personalization and how do I use it?' },
  {
    label: 'Find inactive profiles',
    message:
      "I want to find inactive profiles in my workspace. Can you ask me what criteria or timeframe I'm thinking about?",
  },
  { label: 'What is A/B testing?', message: 'What is A/B testing and how do I set it up?' },
  {
    label: 'Write a re-engagement email',
    message:
      "I want to write a re-engagement email. Can you ask me about my audience and what I'm trying to win back before drafting anything?",
    excludeIf: isPlanBuilder,
  },
  { label: 'What are transactional emails?', message: 'What are transactional emails and how do I send them?' },
  {
    label: 'Create a routine',
    message: 'I want to create a routine. What do you need to know about my use case to help me build the right one?',
  },
  { label: 'What integrations exist?', message: 'What third-party tools can I integrate with Customer.io?' },
  {
    label: (context) => `Analyze ${context.campaign.singular} performance`,
    message: (context) =>
      `I'd like to analyze the performance of ${context.campaign.indefiniteSingular}. What information do you need from me to help with this?`,
    excludeIf: isPlanBuilder,
  },
  { label: 'What new features launched?', message: 'What new features has Customer.io launched recently?' },
  {
    label: 'Create a retention goal',
    message:
      'I want to create a retention goal. What do you need to know about my product and profiles to help me set this up?',
    // Goals-scoped like the four route groups below: the pitch ends at a page `show-goals` off
    // turns into a 404, so it goes with them.
    excludeIf: (context) => isPlanBuilder(context) || isGoalsHidden(context),
  },
  {
    label: 'Contact support',
    message:
      'I need to contact Customer.io support. Can you help me figure out the best way to reach them or describe my issue?',
  },
];

interface PageSuggestionGroup {
  routes: RoutePattern[];
  suggestions: Suggestion[];
}

/**
 * Route-specific suggestions matched by Route patterns.
 *
 * Routes ending with `/*` use prefix matching (any sub-path matches).
 * All other routes use exact matching.
 * Groups are checked in order — the first match wins.
 *
 * Only includes suggestions that are generic enough to work without workspace-specific state.
 *
 * `routes[0]` doubles as the group's analytics identifier (see `getRouteGroup`),
 * so append new patterns rather than prepending or reordering — changing the
 * first entry renames the dimension and breaks continuity in existing reports.
 */
const PAGE_SUGGESTIONS: PageSuggestionGroup[] = [
  // ── Dashboard / Home ──────────────────────────────────────────────────
  {
    routes: ['/workspaces/:workspaceId/journeys/dashboard', '/workspaces/:workspaceId/home/*'],
    suggestions: [
      {
        label: 'Plan a messaging calendar',
        message: (context) =>
          `Help me plan a 30-day messaging calendar based on best practices for ${context.customers.singular} engagement`,
      },
      {
        label: 'Untargeted segments?',
        message: (context) =>
          `How can I find audience segments that are not being targeted by any active ${context.campaign.singular}?`,
        excludeIf: isPlanBuilder,
      },
      {
        label: 'Gap analysis',
        message: (context) =>
          `What's the next ${context.campaign.singular} I should build to fill a gap in my ${context.customers.singular} journey?`,
        excludeIf: isPlanBuilder,
      },
      {
        label: 'Re-engage inactive profiles',
        message: (context) =>
          `What type of ${context.campaign.singular} is most effective to re-engage inactive profiles?`,
        excludeIf: isPlanBuilder,
      },
      { label: 'Measure weekly performance', message: 'How do I set up a weekly summary of my messaging performance?' },
    ],
  },

  // ── Campaign — Workflow Builder ───────────────────────────────────────
  {
    routes: [
      '/workspaces/:workspaceId/journeys/campaigns/:campaignId/setup/workflow/actions',
      '/workspaces/:workspaceId/journeys/campaigns/:campaignId/workflow',
      '/workspaces/:workspaceId/journeys/campaigns/:campaignId/overview/workflow/actions',
      // The migrated builder addresses the same page without the `journeys/campaigns` prefix,
      // which taxonomy normalization does not rewrite. Listed so the page keeps its contextual
      // suggestions and its analytics route group once the rollout flag is on.
      '/workspaces/:workspaceId/automations/:campaignId/workflow',
    ],
    suggestions: [
      {
        label: 'Review my workflow',
        message: (context) =>
          `What are common logical gaps or missing steps to look for when reviewing ${context.campaign.indefiniteSingular} workflow?`,
      },
      { label: 'Ideal email delay', message: "What's the ideal delay between emails in an onboarding sequence?" },
      {
        label: 'Branch on click',
        message: 'How do I add a branch that sends different messages based on whether someone clicked?',
      },
      {
        label: 'Write a subject line',
        message: 'Write a compelling subject line and preview text for an onboarding email',
      },
      {
        label: 'Prevent re-entry',
        message: (context) =>
          `How do I stop someone from re-entering ${context.campaign.indefiniteSingular} if they've already finished it?`,
      },
      // Three shapes of the same capability. All imperative, so the Agent builds
      // the action rather than explaining it; "this" resolves from the campaign
      // context the workflow builder registers.
      {
        group: LLM_ACTION_GROUP,
        label: 'Recap recent activity',
        message: (context) =>
          `Add an LLM action to this ${context.campaign.singular} that summarizes each ${context.customers.singular}'s recent activity into an attribute I can use in later messages.`,
      },
      {
        group: LLM_ACTION_GROUP,
        label: (context) => `Score each ${context.people.singular}`,
        message: (context) =>
          `Add an LLM action to this ${context.campaign.singular} that scores how likely each ${context.customers.singular} is to convert, so I can branch on the result.`,
      },
      {
        group: LLM_ACTION_GROUP,
        label: 'Write a personal intro',
        message: (context) =>
          `Add an LLM action to this ${context.campaign.singular} that writes a short intro paragraph tailored to each ${context.customers.singular}, then use it in the next email.`,
      },
    ],
  },

  // ── Campaigns — List / New / Welcome (before detail — /new matches :campaignId)
  {
    routes: [
      '/workspaces/:workspaceId/journeys/campaigns',
      '/workspaces/:workspaceId/journeys/campaigns/new',
      '/workspaces/:workspaceId/journeys/campaigns/welcome/*',
    ],
    suggestions: [
      {
        label: 'Re-engage inactive profiles',
        message: (context) =>
          `What type of ${context.campaign.singular} is most effective to re-engage inactive profiles?`,
      },
      {
        label: 'Onboarding sequence',
        message: (context) =>
          `What's the ideal ${context.campaign.singular} sequence for a new profile's first 60 days?`,
      },
      {
        label: (context) => `Event-triggered ${context.campaign.plural}`,
        message: (context) => `How do I set up event-triggered ${context.campaign.plural} in Customer.io?`,
      },
      {
        label: 'Clean up old drafts',
        message: (context) =>
          `How do I find and clean up ${context.campaign.plural} that have been in draft for a long time?`,
      },
      {
        label: (context) => `${context.campaign.titleSingular} best practices`,
        message: (context) =>
          `What are the best practices for organizing and naming ${context.campaign.plural} in Customer.io?`,
      },
    ],
  },

  // ── Campaign — Detail / Overview ──────────────────────────────────────
  {
    routes: ['/workspaces/:workspaceId/journeys/campaigns/:campaignId/*'],
    suggestions: [
      {
        label: 'Improve conversions',
        message: (context) =>
          `What are 3 specific changes I could make to improve conversions on ${context.campaign.indefiniteSingular}?`,
      },
      {
        label: 'Diagnose open rate drop',
        message: 'What are common reasons for an email open rate drop and how do I fix it?',
      },
      {
        label: 'Reduce drop-off',
        message: 'How do I identify which message in a workflow is causing the most drop-off?',
      },
      {
        label: 'A/B test ideas',
        message: (context) =>
          `Help me design an A/B test hypothesis for improving ${context.campaign.indefiniteSingular} workflow`,
      },
      {
        label: 'Add a conversion goal',
        message: (context) =>
          `What goal or conversion event should I add to make my ${context.campaign.singular} smarter?`,
      },
    ],
  },

  // ── Broadcast / Newsletter — Detail ───────────────────────────────────
  {
    routes: [
      '/workspaces/:workspaceId/journeys/broadcasts/broadcast/:broadcastId/*',
      '/workspaces/:workspaceId/journeys/broadcasts/newsletter/:newsletterId/*',
    ],
    suggestions: [
      {
        label: 'Follow-up message',
        message: (context) =>
          `Draft a follow-up message for ${context.people.plural} ${context.people.relativePronoun} opened a broadcast but did not click`,
      },
      { label: 'Subject line variants', message: 'Suggest 3 subject line variants I could test in my next broadcast' },
      { label: 'Compare performance', message: "How do I compare a broadcast's performance to my previous sends?" },
      {
        label: 'Improve next send',
        message: 'What would I change about a broadcast to improve results on the next send?',
      },
      {
        label: 'Segment targeting',
        message: 'How do I determine which audience segments respond best to a broadcast?',
      },
    ],
  },

  // ── Broadcasts — List ─────────────────────────────────────────────────
  {
    routes: ['/workspaces/:workspaceId/journeys/broadcasts/*'],
    suggestions: [
      {
        label: 'Best send time',
        message: "What's the best day and time to send a broadcast based on general engagement patterns?",
      },
      {
        label: 'Re-engagement broadcast',
        message: 'Help me plan a re-engagement broadcast: who should I target and what should I say?',
      },
      { label: 'Broadcast calendar', message: 'Help me plan a broadcast calendar for the next 8 weeks' },
      {
        label: (context) => `Broadcast vs ${context.campaign.singular}`,
        message: (context) => `When should I use a broadcast vs a triggered ${context.campaign.singular}?`,
        excludeIf: isPlanBuilder,
      },
      { label: 'Newsletter best practices', message: 'What are best practices for newsletters in Customer.io?' },
    ],
  },

  // ── Segments — List / New / Explore (before detail — /new matches :segmentId)
  {
    routes: [
      '/workspaces/:workspaceId/journeys/segments',
      '/workspaces/:workspaceId/journeys/segments/new',
      '/workspaces/:workspaceId/journeys/segments/explore/*',
    ],
    suggestions: [
      {
        label: 'Consolidate segments',
        message: 'How can I find segments that overlap significantly and could be consolidated?',
      },
      {
        label: 'Build a churn segment',
        message: 'Build me a segment of profiles who signed up 30+ days ago but have never converted',
      },
      {
        label: 'Launch segmentation',
        message: 'Help me design a segmentation strategy for an upcoming product launch',
      },
      {
        label: 'Unused segments',
        message: (context) =>
          `How do I find segments that are not being used in any active ${context.campaign.singular}?`,
        excludeIf: isPlanBuilder,
      },
      { label: 'Segment best practices', message: 'What are best practices for organizing and maintaining segments?' },
    ],
  },

  // ── Segment — Detail ──────────────────────────────────────────────────
  {
    routes: ['/workspaces/:workspaceId/journeys/segments/:segmentId/*'],
    suggestions: [
      { label: 'Tighten conditions', message: 'How can I tighten segment conditions to make it more precise?' },
      {
        label: 'Draft a targeted email',
        message: 'Draft an email that would resonate specifically with a highly engaged audience segment',
      },
      {
        label: (context) => `Best ${context.campaign.singular} for segment`,
        message: (context) =>
          `What's the best ${context.campaign.singular} type I could run against a specific segment?`,
        excludeIf: isPlanBuilder,
      },
      { label: 'Segment too small?', message: 'What are common reasons a segment might be smaller than expected?' },
      {
        label: (context) => `${context.campaign.titlePlural} using segment`,
        message: (context) => `How do I find which ${context.campaign.plural} are currently using a specific segment?`,
        excludeIf: isPlanBuilder,
      },
    ],
  },

  // ── People — List / New / Merge (before detail — /new matches :personId)
  {
    routes: [
      '/workspaces/:workspaceId/journeys/people',
      '/workspaces/:workspaceId/journeys/people/new',
      '/workspaces/:workspaceId/journeys/people/create',
      '/workspaces/:workspaceId/journeys/people/merge',
      '/workspaces/:workspaceId/journeys/people/welcome',
      // The migrated URLs, in both taxonomy spellings. `normalizeJourneysPathname`
      // canonicalizes the `profiles` spelling but never inserts a missing `journeys`
      // segment, so these have to be listed alongside the Ember paths to match.
      '/workspaces/:workspaceId/people',
      '/workspaces/:workspaceId/people/create',
      '/workspaces/:workspaceId/people/new',
      '/workspaces/:workspaceId/people/merge',
      '/workspaces/:workspaceId/people/welcome',
      '/workspaces/:workspaceId/profiles',
      '/workspaces/:workspaceId/profiles/create',
      '/workspaces/:workspaceId/profiles/new',
      '/workspaces/:workspaceId/profiles/merge',
      '/workspaces/:workspaceId/profiles/welcome',
    ],
    suggestions: [
      {
        label: 'Find unconverted profiles',
        message: 'How do I find profiles who signed up a long time ago but have never converted?',
      },
      { label: 'Expiring subscriptions', message: 'How do I find profiles whose subscription or trial expires soon?' },
      {
        label: 'Unsubscribe patterns',
        message: (context) =>
          `How can I identify patterns among ${context.customers.plural} who have recently unsubscribed?`,
      },
      {
        label: 'Incomplete profiles',
        message: 'How do I find incomplete profiles missing key attributes?',
      },
      {
        label: (context) => `Import ${context.people.plural}`,
        message: (context) => `How do I import ${context.people.plural} into Customer.io?`,
      },
    ],
  },

  // ── Person — Detail ───────────────────────────────────────────────────
  {
    routes: ['/workspaces/:workspaceId/journeys/people/:personId/*'],
    suggestions: [
      {
        label: 'Activity summary',
        message: (context) => `How do I interpret a ${context.people.singular}'s activity timeline in Customer.io?`,
      },
      {
        label: (context) => `${context.campaign.titleSingular} engagement`,
        message: (context) =>
          `How can I see which ${context.campaign.plural} a specific ${context.people.singular} has received and engaged with?`,
        excludeIf: isPlanBuilder,
      },
      {
        label: 'Active workflows',
        message: (context) =>
          `How do I check if a ${context.people.singular} is currently inside any active ${context.campaign.singular} workflows?`,
        excludeIf: isPlanBuilder,
      },
      {
        label: 'Re-engage this profile',
        message: "What's the best approach to re-engage a profile based on their behavior history?",
      },
      {
        label: 'Why did they unsubscribe?',
        message: (context) =>
          `What can a ${context.people.singular}'s history tell me about why they might have unsubscribed?`,
      },
    ],
  },

  // ── Content / Templates ───────────────────────────────────────────────
  {
    routes: [
      '/workspaces/:workspaceId/journeys/message-library',
      '/workspaces/:workspaceId/assets',
      '/workspaces/:workspaceId/journeys/layouts/*',
      '/workspaces/:workspaceId/snippets/*',
      '/workspaces/:workspaceId/journeys/template/:templateId',
    ],
    suggestions: [
      {
        label: 'Archive unused templates',
        message: (context) => `How can I find templates that are not used in any active ${context.campaign.singular}?`,
        excludeIf: isPlanBuilder,
      },
      {
        label: 'Win-back email series',
        message: 'Create a template outline for a 3-part win-back email series',
        excludeIf: isPlanBuilder,
      },
      {
        label: 'Template consistency',
        message: (context) =>
          `What are best practices for keeping email templates consistent across ${context.campaign.plural}?`,
        excludeIf: isPlanBuilder,
      },
      { label: 'Launch templates', message: 'What templates do I need for an upcoming product launch?' },
      { label: 'Broken links', message: 'How do I check my templates for outdated copy or broken links?' },
    ],
  },

  // ── Design Studio — WhatsApp editor (before general DS — first match wins) ──
  {
    routes: ['/workspaces/:workspaceId/design-studio/editor/whatsapp/*'],
    suggestions: [],
  },

  // ── Design Studio ─────────────────────────────────────────────────────
  {
    routes: [
      '/workspaces/:workspaceId/design-studio/dashboard',
      '/workspaces/:workspaceId/design-studio/editor/*',
      '/workspaces/:workspaceId/design-studio/welcome',
      '/workspaces/:workspaceId/design-studio/global-styles',
    ],
    suggestions: [
      { label: 'Spellcheck', message: 'Check my email for spelling and grammar mistakes and fix any issues you find.' },
      { label: 'Improve subject line', message: 'How do I write a compelling email subject line and preview text?' },
      { label: 'Rewrite for clarity', message: 'How do I make email copy more concise and action-oriented?' },
      { label: 'A/B test version', message: 'How do I write an alternative version of an email for an A/B test?' },
      {
        label: 'Write a strong CTA',
        message: 'What are best practices for writing effective call-to-action buttons in emails?',
      },
      {
        label: 'Conditional content',
        message: 'How do I add conditional content blocks that show different content based on profile attributes?',
      },
    ],
  },

  // ── Transactional Messages ────────────────────────────────────────────
  {
    routes: ['/workspaces/:workspaceId/journeys/transactional/*'],
    suggestions: [
      {
        label: 'Password reset email',
        message: 'How do I write a clear, on-brand password reset email that reduces friction?',
      },
      {
        label: 'Personalization variables',
        message: 'What personalization variables should I include in transactional messages?',
      },
      {
        label: 'Fallback values',
        message: 'How do I add a safe fallback for when a personalization variable is missing?',
      },
      {
        label: 'Test without sending',
        message: (context) =>
          `How do I test a transactional message without sending it to a real ${context.customers.singular}?`,
      },
      {
        label: 'Transactional vs marketing',
        message: 'What is the difference between transactional and marketing messages?',
      },
    ],
  },

  // ── Analysis / Metrics ────────────────────────────────────────────────
  {
    // The renamed /journeys/reports segment (Analysis → Reports) is handled by
    // normalizeJourneysPathname before matching, so only the canonical path is listed.
    routes: ['/workspaces/:workspaceId/journeys/analysis', '/workspaces/:workspaceId/analysis/deliverability'],
    suggestions: [
      {
        label: 'Deliverability health',
        message: 'What does good email deliverability look like and what are red flags to watch for?',
      },
      { label: 'High bounce rate', message: 'My bounce rate looks high. What does that mean and what should I do?' },
      {
        label: (context) => `Best performing ${context.campaign.plural}`,
        message: (context) => `How do I identify which ${context.campaign.plural} are performing best and why?`,
        excludeIf: isPlanBuilder,
      },
      {
        label: 'Engagement by segment',
        message: 'How do I compare engagement rates across different audience segments?',
        excludeIf: isPlanBuilder,
      },
      { label: 'Weekly summary', message: 'How do I build a weekly performance summary to share with my team?' },
    ],
  },

  // ── Data & Integrations ───────────────────────────────────────────────
  {
    routes: [
      // Both spellings while the data-index migration rolls out, for the same
      // reason as imports below: the embedded app still serves the `journeys`
      // one for an account the flag has not reached. Wildcards, not `:type`,
      // so an item page keeps its suggestions too.
      '/workspaces/:workspaceId/data-index/*',
      '/workspaces/:workspaceId/journeys/data-index/*',
      '/workspaces/:workspaceId/journeys/integrations/*',
      // Both spellings while the imports migration rolls out: the embedded app still serves the
      // `journeys` one for an account the flag has not reached.
      '/workspaces/:workspaceId/imports/*',
      '/workspaces/:workspaceId/journeys/imports/*',
      '/workspaces/:workspaceId/journeys/exports/*',
      '/workspaces/:workspaceId/journeys/collections/*',
    ],
    suggestions: [
      {
        label: 'Unused events',
        message: (context) =>
          `How do I find tracked events that are not being used in any ${context.campaign.singular} trigger?`,
        excludeIf: isPlanBuilder,
      },
      {
        label: 'Event schema design',
        message: 'Help me design an event schema for tracking steps in an onboarding flow',
      },
      {
        label: 'Churn prevention data',
        message: (context) => `What data do I need to power a churn prevention ${context.campaign.singular}?`,
        excludeIf: isPlanBuilder,
      },
      {
        label: 'Verify event data',
        message: 'How do I verify that event data is arriving correctly and in the right format?',
      },
      {
        label: 'Stale events',
        message: 'How can I identify tracked events that have not fired recently and might be broken?',
      },
    ],
  },

  // ── Settings ──────────────────────────────────────────────────────────
  {
    routes: [
      '/workspaces/:workspaceId/journeys/settings/*',
      '/workspaces/:workspaceId/settings/ai-business-profile',
      '/workspaces/:workspaceId/settings/audit-logs',
      '/settings/audit-logs',
      '/settings/account/*',
      '/settings/team/*',
      '/settings/billing/*',
      '/settings/security/*',
      '/settings/personal/*',
      '/settings/roles/*',
      '/settings/privacy',
      '/settings/ai',
      '/settings/workspaces/*',
      '/settings/subscription',
      '/settings/receipts',
      '/settings/api_credentials',
      '/settings/experimental-features',
    ],
    suggestions: [
      {
        label: 'Sending domain setup',
        message: 'What sending domain settings should I configure to maximize deliverability?',
      },
      {
        label: 'Custom unsubscribe page',
        message: 'How do I set up a custom unsubscribe page that matches our brand?',
      },
      {
        label: 'Team permissions',
        message: 'How do I add a team member and which permission level should I give them?',
      },
      {
        label: 'Subscription topics',
        message: 'What is the difference between subscription topics and a global unsubscribe?',
      },
      { label: 'GDPR compliance', message: 'What do I need to configure in my workspace to be GDPR-compliant?' },
    ],
  },

  // ── Goals — Milestones (before detail — /milestones is more specific) ─
  // All four goals groups below are wrapped in `goalsOnly`: `show-goals` off, these URLs render
  // Hydra's NotFoundPage (FLO-4772), so their pitches must not appear next to it.
  {
    routes: ['/workspaces/:workspaceId/journeys/goals/:goalId/milestones'],
    suggestions: goalsOnly([
      {
        label: 'What are milestones?',
        message: 'What is a milestone in goals and how does the milestone-to-goal funnel view work?',
      },
      {
        label: 'High drop-off rate',
        message: (context) =>
          `What are common reasons ${context.people.plural} complete a milestone but don't go on to achieve the goal, and how do I address them?`,
      },
      {
        label: 'Pick a good milestone',
        message: 'How do I choose the right milestone event? What behaviors tend to predict goal achievement?',
      },
      {
        label: 'Improve funnel conversion',
        message:
          'What are proven strategies to improve conversion between a milestone and a goal in a lifecycle marketing flow?',
      },
      {
        label: 'Milestone vs goal event',
        message:
          "What's the difference between a milestone event and a goal event, and can the same event be used as both?",
      },
    ]),
  },

  // ── Goals — New (before detail — /new matches :goalId) ─────────────
  {
    routes: ['/workspaces/:workspaceId/journeys/goals/new'],
    suggestions: goalsOnly([
      {
        label: 'Help me configure this',
        message: 'Walk me through the key decisions I need to make when creating a new goal in Customer.io.',
      },
      {
        label: 'Event or segment?',
        message: "What's the difference between an event-based and segment-based goal, and when should I use each?",
      },
      {
        label: 'Track revenue impact',
        message:
          'How do I configure a goal to track revenue impact, and when does tracking revenue value matter versus just tracking volume?',
      },
      {
        label: 'How does impact work?',
        message: 'What is impact value in goals, how is it calculated, and when should I bother tracking it?',
      },
      {
        label: 'Am I on the right track?',
        message:
          "I'm setting up a new goal. How do I validate that my goal event, impact settings, and milestone configuration make sense before saving?",
      },
    ]),
  },

  // ── Goals — Detail / Overview ──────────────────────────────────────
  {
    routes: ['/workspaces/:workspaceId/journeys/goals/:goalId/*'],
    suggestions: goalsOnly([
      {
        label: 'How does attribution work?',
        message: (context) =>
          `How does attribution work in goals? How does Customer.io decide which ${context.campaign.singular} gets credit for a goal completion?`,
      },
      {
        label: 'Unattributed completions?',
        message: 'What does it mean when I see unattributed goal completions, and what should I do with that insight?',
      },
      {
        label: 'Improve completion rate',
        message: (context) =>
          `What are the most effective ways to increase goal completion rate using ${context.campaign.plural} and broadcasts in Customer.io?`,
      },
      {
        label: 'Add more sources',
        message: (context) =>
          `How do I decide which ${context.campaign.plural} and broadcasts to connect to a goal as sources?`,
      },
      {
        label: 'Impact vs completions',
        message: "What's the difference between goal completions and impact value, and when does each matter more?",
      },
    ]),
  },

  // ── Goals — List ───────────────────────────────────────────────────
  {
    routes: ['/workspaces/:workspaceId/journeys/goals'],
    suggestions: goalsOnly([
      {
        label: 'What are goals?',
        message: (context) =>
          `What are goals in Customer.io and how is it different from setting a goal inside ${context.campaign.indefiniteSingular}?`,
      },
      {
        label: 'Why use goals?',
        message: (context) =>
          `What problem do goals solve that I can't solve with ${context.campaign.singular}-level conversion tracking?`,
      },
      {
        label: 'What makes a good goal?',
        message:
          'What are the characteristics of a well-defined goal, and how do I know if something is a good candidate?',
      },
      {
        label: 'Suggest a goal',
        message:
          'What are some good goals I should track based on common lifecycle marketing use cases like activation, retention, or re-engagement?',
      },
      {
        label: 'Goals best practices',
        message: 'What are best practices for organizing and naming goals in Customer.io?',
      },
    ]),
  },

  // ── Pipelines (CDP) ───────────────────────────────────────────────────
  {
    routes: [
      '/workspaces/:workspaceId/pipelines/setup-guide/*',
      '/workspaces/:workspaceId/pipelines/sources/*',
      '/workspaces/:workspaceId/pipelines/destinations/*',
      '/workspaces/:workspaceId/pipelines/connections',
      '/workspaces/:workspaceId/pipelines/catalog',
    ],
    suggestions: [
      { label: 'What are pipelines?', message: 'What is Customer.io Data Pipelines and how does it work?' },
      { label: 'Add a source', message: 'How do I add a new data source in Data Pipelines?' },
      { label: 'Add a destination', message: 'How do I connect a destination to send data to?' },
      {
        label: 'Source vs destination',
        message: 'What is the difference between a source and a destination in Data Pipelines?',
      },
      { label: 'Reverse ETL', message: 'What is reverse ETL and how do I set it up in Customer.io?' },
    ],
  },
];

/** Analytics value for a pathname that matches no route group. */
export const NO_ROUTE_GROUP = 'none';

/**
 * Which pool the displayed suggestions were drawn from. `static-generic` covers
 * both "no route matched" and "the matched group was entirely plan-excluded",
 * which is why it can't be inferred from the route group alone.
 */
export type SuggestionSource = 'static-route' | 'static-generic' | 'dynamic';

type InitialStaticSuggestions = {
  suggestions: ResolvedSuggestion[];
  source: SuggestionSource;
};

const MARQUEE_ROW_COUNT = 3;
/** Number of route-specific suggestions to mix into the marquee when a route matches */
const ROUTE_SPECIFIC_COUNT = 8;
/** Number of generic suggestions to mix in alongside route-specific ones */
const GENERIC_MIX_COUNT = 12;

/** Plan gating, applied identically wherever a pool is drawn. */
function eligibleSuggestions(suggestions: readonly Suggestion[], context: SuggestionContext): Suggestion[] {
  return suggestions.filter((s) => s.excludeIf == null || !s.excludeIf(context));
}

/**
 * Get 3 random static suggestions for initial page load (used in next-message suggestions).
 * When a pathname is provided, returns route-specific suggestions if available.
 */
export function getInitialStaticSuggestions(
  pathname: string | undefined,
  {
    isBuilder = false,
    showGoals = false,
    taxonomy,
  }: { isBuilder?: boolean; showGoals?: boolean; taxonomy: ProductTaxonomy },
) {
  const context: SuggestionContext = {
    isBuilder,
    showGoals,
    campaign: buildCampaignCopy(taxonomy),
    people: buildPeopleCopy(taxonomy),
    customers: buildCustomersCopy(taxonomy),
  };
  const filterEligible = (suggestions: Suggestion[]): Suggestion[] => eligibleSuggestions(suggestions, context);
  // Filter before deciding which pool to draw from, so a route group whose
  // suggestions are all plan-excluded falls back to STATIC_SUGGESTIONS instead
  // of returning fewer than the intended count.
  const routeSuggestions = pathname != null ? filterEligible(getRouteSuggestions(pathname)) : [];
  const isRoutePool = routeSuggestions.length > 0;
  const pool = isRoutePool ? routeSuggestions : filterEligible(STATIC_SUGGESTIONS);
  const shuffled = shuffle(collapseGroups(pool));

  return {
    suggestions: shuffled.slice(0, 3).map((s) => resolveSuggestion(s, context)),
    source: isRoutePool ? 'static-route' : 'static-generic',
  } satisfies InitialStaticSuggestions;
}

/**
 * Fisher-Yates. `toSorted(() => Math.random() - 0.5)` is not a fair shuffle —
 * the engine's sort makes an element's final position depend on where it
 * started, which matters here because collapsed group representatives are
 * appended last.
 */
function shuffle<T>(items: readonly T[]): T[] {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = result[i];
    const b = result[j];
    if (a != null && b != null) {
      result[i] = b;
      result[j] = a;
    }
  }
  return result;
}

/**
 * Reduce each group to one randomly chosen member, so a capability with several
 * alternative pitches competes for a single slot rather than crowding the bar.
 */
function collapseGroups(suggestions: Suggestion[]): Suggestion[] {
  const groups = new Map<string, Suggestion[]>();
  const ungrouped: Suggestion[] = [];

  for (const suggestion of suggestions) {
    if (suggestion.group == null) {
      ungrouped.push(suggestion);
      continue;
    }
    groups.set(suggestion.group, [...(groups.get(suggestion.group) ?? []), suggestion]);
  }

  const representatives = [...groups.values()].flatMap((members) => {
    const chosen = members[Math.floor(Math.random() * members.length)];
    return chosen == null ? [] : [chosen];
  });

  return [...ungrouped, ...representatives];
}

export const HUMAN_SUPPORT_SUGGESTION = {
  label: 'Human support',
  message: 'Talk to a human support agent',
} as const;

export interface IndexedSuggestion extends ResolvedSuggestion {
  index: number;
}

/**
 * Find the first route group matching the pathname, or null when none match.
 */
function findRouteGroup(pathname: string): PageSuggestionGroup | null {
  // PAGE_SUGGESTIONS patterns use the canonical /journeys/campaigns paths, so
  // normalize first to also match the renamed /journeys/automations URLs.
  const normalizedPathname = normalizeJourneysPathname(pathname);
  for (const group of PAGE_SUGGESTIONS) {
    if (matchRoutes(normalizedPathname, group.routes)) {
      return group;
    }
  }
  return null;
}

/**
 * Get route-specific suggestions for the current pathname.
 * Checks each group in order and returns the first match.
 */
function getRouteSuggestions(pathname: string): Suggestion[] {
  return findRouteGroup(pathname)?.suggestions ?? [];
}

/**
 * Stable analytics identifier for the page a suggestion was shown on. Uses the
 * matched group's first route pattern so new groups need no hand-written id.
 */
export function getRouteGroup(pathname: string | undefined): string {
  if (pathname == null) {
    return NO_ROUTE_GROUP;
  }
  return findRouteGroup(pathname)?.routes[0] ?? NO_ROUTE_GROUP;
}

/**
 * Get static suggestions distributed into rows for the marquee animation.
 * Each suggestion gets a unique index for analytics tracking.
 *
 * When a pathname is provided and matches a route group:
 * - Mixes route-specific suggestions with a subset of generic ones
 * - Route-specific suggestions are prioritized
 *
 * When no pathname is provided or no route matches:
 * - Falls back to the full set of generic suggestions
 */
export function getMarqueeSuggestionRows({
  shouldIncludeHumanSupport = false,
  pathname,
  isBuilder = false,
  showGoals = false,
  taxonomy,
}: {
  shouldIncludeHumanSupport?: boolean;
  pathname?: string;
  isBuilder?: boolean;
  showGoals?: boolean;
  taxonomy: ProductTaxonomy;
}): IndexedSuggestion[][] {
  const context: SuggestionContext = {
    isBuilder,
    showGoals,
    campaign: buildCampaignCopy(taxonomy),
    people: buildPeopleCopy(taxonomy),
    customers: buildCustomersCopy(taxonomy),
  };
  const filterEligible = (suggestions: Suggestion[]): Suggestion[] =>
    collapseGroups(eligibleSuggestions(suggestions, context));

  const routeSuggestions = filterEligible(pathname != null ? getRouteSuggestions(pathname) : []);
  const staticSuggestions = filterEligible(STATIC_SUGGESTIONS);

  let pool: Suggestion[];

  if (routeSuggestions.length > 0) {
    // Mix route-specific suggestions with a subset of generic ones. Dedupe on the
    // resolved label so route and generic copies of the same suggestion collapse.
    const shuffledRoute = shuffle(routeSuggestions);
    const routeLabels = new Set(shuffledRoute.map((s) => resolveCopy(s.label, context)));
    const genericPool = staticSuggestions.filter((s) => !routeLabels.has(resolveCopy(s.label, context)));
    const shuffledGeneric = shuffle(genericPool);

    pool = [...shuffledRoute.slice(0, ROUTE_SPECIFIC_COUNT), ...shuffledGeneric.slice(0, GENERIC_MIX_COUNT)];
  } else {
    pool = [...staticSuggestions];
  }

  if (shouldIncludeHumanSupport) {
    pool.push(HUMAN_SUPPORT_SUGGESTION);
  }

  const shuffled = shuffle(pool);
  const indexed = shuffled.map((s, index) => ({ ...resolveSuggestion(s, context), index: index + 1 }));
  const rows: IndexedSuggestion[][] = [];
  const perRow = Math.ceil(indexed.length / MARQUEE_ROW_COUNT);

  for (let index = 0; index < MARQUEE_ROW_COUNT; index++) {
    rows.push(indexed.slice(index * perRow, (index + 1) * perRow));
  }

  return rows;
}
